import { useRef, useState } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { registrationIndividual } from '@/api/auth.api';
import { signIn } from '@/redux/auth/auth.slice';
import { useAppDispatch } from '@/redux/store.hook';
import { getAuthRefreshDeadline } from '@/utils/authToken';
import useAuthValidation from '@/hooks/requireSchema/useAuthValidation';
import useCurrentLang from '@/hooks/useCurrentLang';
import useResize from '@/hooks/useResize';
import { FormBox } from '@/layout/Box';
import { Button } from '@/components/UI/Button';
import { InputWrapper, FormTitle } from '@/components/Container/AuthContainer/styles';
import TermsModal from '@/components/Account/RegisterForm/TermsModal';
import { resolveDmpPurchaseReturnPath, withDmpPurchaseReturnPath } from './purchaseReturnPath';

// 本表單只使用純文字翻譯；避免舊版全站 Resources 型別在 CRA 中遞迴展開。
const useRegistrationTranslation = useTranslation as unknown as () => { t: (key: string) => string };

const RegistrationFormBox = styled(FormBox)`
  font-size: ${(p) => p.theme.fontSizes.base};
  line-height: 1.6;
  > p {
    margin: 16px 0 28px;
  }
  > form {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
  }
  ${InputWrapper} {
    margin-bottom: 0;
  }
  .registration-consents {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .MuiFormControlLabel-root {
    margin: 0;
    align-items: flex-start;
  }
  .MuiFormControlLabel-label {
    line-height: 1.6;
    padding-top: 8px;
    overflow-wrap: anywhere;
  }
  .MuiCheckbox-root {
    margin-left: -9px;
    flex-shrink: 0;
  }
  .registration-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  .registration-alternatives {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #e5e7eb;
  }
  button {
    min-height: 44px;
    white-space: normal;
    height: auto;
  }
  @media (max-width: 600px) {
    .registration-actions,
    .registration-alternatives {
      flex-direction: column;
    }
    .registration-actions > button,
    .registration-alternatives > button {
      width: 100%;
    }
  }
`;

export default function IndividualQuickRegistration({
  email,
  proof,
}: {
  email: string;
  proof: string;
  chooseKind: (kind: 'company' | 'barrister') => void;
}) {
  const { t } = useRegistrationTranslation();
  const en = useCurrentLang() === 'en';
  const { isTablet } = useResize();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const dispatch = useAppDispatch();
  const { passwordRegister } = useAuthValidation();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ first: string; last: string; password: string; confirmation: string }>();
  const [accepted, setAccepted] = useState({ infoCollect: false, terms: false, transfer: false });
  const [open, setOpen] = useState({ infoCollect: false, terms: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const locked = useRef(false);
  const submit = handleSubmit(async (data) => {
    if (locked.current || !accepted.infoCollect || !accepted.terms || !accepted.transfer) return;
    locked.current = true;
    setBusy(true);
    setError('');
    try {
      const response = (
        await registrationIndividual({
          email,
          acc_firstName: data.first.trim(),
          acc_lastName: data.last.trim(),
          password: CryptoJS.MD5(data.password).toString(),
          registration_token: proof,
          tcAgreement: accepted.terms,
          PICsAgreement: accepted.infoCollect,
          dataTransferAgreement: accepted.transfer,
        })
      ).data;
      if (!response.success) throw new Error('registration_failed');
      const result = response.data;
      await dispatch(
        signIn({
          isLogin: true,
          accessToken: result.access_token,
          userData: result.user,
          applications: result.applications,
          registDone: true,
          refreshTokenTime: getAuthRefreshDeadline(result.expires_in),
        })
      );
      navigate(
        resolveDmpPurchaseReturnPath(params.get('path'), String(result.user.id)) || '/profile/subscriptionDetail',
        { replace: true }
      );
    } catch {
      setError(
        en
          ? 'Registration could not be completed. Check your details or verify your email again if verification has expired.'
          : '暫時無法完成註冊，請確認資料；若驗證已過期，請重新驗證電子郵件。'
      );
    } finally {
      locked.current = false;
      setBusy(false);
    }
  });
  return (
    <RegistrationFormBox
      as="section"
      isTablet={isTablet}
      aria-label={en ? 'Individual account registration' : '個人帳號註冊'}
    >
      <FormTitle>{en ? 'Create your individual account' : '建立個人帳號'}</FormTitle>
      <p>
        {en
          ? 'Verification is complete. Complete these details to continue your purchase. Additional profile information can be added later.'
          : '驗證已完成，填寫以下資料即可繼續購買；其他個人資料可稍後補齊。'}
      </p>
      <form onSubmit={submit} noValidate aria-label={en ? 'Account details' : '帳號資料'}>
        <InputWrapper>
          <TextField fullWidth label={t('form.email.label')} value={email} disabled />
        </InputWrapper>
        <InputWrapper>
          <TextField
            fullWidth
            label={t('form.giveName.label')}
            autoComplete="given-name"
            {...register('first', { required: true, validate: (v) => !!v.trim() })}
            error={!!errors.first}
          />
        </InputWrapper>
        <InputWrapper>
          <TextField
            fullWidth
            label={t('form.familyName.label')}
            autoComplete="family-name"
            {...register('last', { required: true, validate: (v) => !!v.trim() })}
            error={!!errors.last}
          />
        </InputWrapper>
        <InputWrapper>
          <TextField
            fullWidth
            label={t('form.password.label')}
            type="password"
            autoComplete="new-password"
            {...register('password', passwordRegister)}
            error={!!errors.password}
            helperText={errors.password?.message}
          />
        </InputWrapper>
        <InputWrapper>
          <TextField
            fullWidth
            label={en ? 'Confirm password' : '確認密碼'}
            type="password"
            autoComplete="new-password"
            {...register('confirmation', { required: true, validate: (v) => v === watch('password') })}
            error={!!errors.confirmation}
          />
        </InputWrapper>
        <div className="registration-consents">
          <FormControlLabel
            control={
              <Checkbox checked={accepted.infoCollect} onChange={() => setOpen({ ...open, infoCollect: true })} />
            }
            label={[t('form.terms.label.pic.prev'), t('form.terms.label.pic.last')].join(en ? ' ' : '')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={accepted.transfer}
                onChange={(_, checked) => setAccepted({ ...accepted, transfer: checked })}
              />
            }
            label={t('form.terms.label.dataShare')}
          />
          <FormControlLabel
            control={<Checkbox checked={accepted.terms} onChange={() => setOpen({ ...open, terms: true })} />}
            label={[t('form.terms.label.t&c.prev'), t('form.terms.label.t&c.last')].join(en ? ' ' : '')}
          />
        </div>
        {error && <p role="alert">{error}</p>}
        <div className="registration-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={busy || !accepted.infoCollect || !accepted.terms || !accepted.transfer}
          >
            {busy ? t('loading') : en ? 'Create account and continue' : '建立帳號並繼續'}
          </Button>
          {error && (
            <Button
              type="button"
              variant="primary-invert"
              onClick={() => navigate(withDmpPurchaseReturnPath('/auth/signup', params.get('path')))}
            >
              {en ? 'Verify email again' : '重新驗證電子郵件'}
            </Button>
          )}
        </div>
      </form>
      <TermsModal
        open={open}
        handleClose={(key) => setOpen({ ...open, [key]: false })}
        handleChange={(key, current) => {
          setAccepted({ ...accepted, [key]: !current });
          setOpen({ ...open, [key]: false });
        }}
      />
    </RegistrationFormBox>
  );
}
